import { useEffect } from 'react';
import { Button, Card, Col, Form, Input, Row, Space, message } from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../../api/endpoints/users';
import { authApi } from '../../../api/endpoints/auth';
import { PageHeader } from '../../../components/common/PageHeader';

const profileSchema = z.object({
  firstName: z.string().min(1).max(60),
  lastName: z.string().min(1).max(60),
  shortName: z.string().max(20).optional().or(z.literal('')),
  phone: z.string().max(30).optional().or(z.literal('')),
});

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Requerida'),
    newPassword: z
      .string()
      .min(10, 'Mínimo 10 caracteres')
      .regex(/[A-Z]/, 'Debe incluir una mayúscula')
      .regex(/[0-9]/, 'Debe incluir un número'),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'No coincide con la nueva contraseña',
    path: ['confirmPassword'],
  });

type ProfileValues = z.infer<typeof profileSchema>;
type PasswordValues = z.infer<typeof passwordSchema>;

export function ProfilePage(): JSX.Element {
  const queryClient = useQueryClient();

  const { data: me } = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => usersApi.myProfile(),
  });

  const profileForm = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { firstName: '', lastName: '', shortName: '', phone: '' },
  });

  const passwordForm = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  });

  useEffect(() => {
    if (me) {
      profileForm.reset({
        firstName: me.firstName,
        lastName: me.lastName,
        shortName: me.shortName ?? '',
        phone: me.phone ?? '',
      });
    }
  }, [me, profileForm]);

  const profileMutation = useMutation({
    mutationFn: (v: ProfileValues) =>
      usersApi.updateOwn({
        firstName: v.firstName,
        lastName: v.lastName,
        shortName: v.shortName || undefined,
        phone: v.phone || undefined,
      }),
    onSuccess: () => {
      message.success('Perfil actualizado');
      void queryClient.invalidateQueries({ queryKey: ['me', 'profile'] });
    },
    onError: () => message.error('No se pudo actualizar el perfil'),
  });

  const passwordMutation = useMutation({
    mutationFn: (v: PasswordValues) => authApi.changePassword(v.currentPassword, v.newPassword),
    onSuccess: () => {
      message.success('Contraseña actualizada. Vuelve a iniciar sesión.');
      passwordForm.reset();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo cambiar la contraseña';
      message.error(msg);
    },
  });

  return (
    <>
      <PageHeader title="Mi perfil" crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Perfil' }]} />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Datos personales">
            <Form
              layout="vertical"
              onFinish={profileForm.handleSubmit((v) => profileMutation.mutate(v))}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Controller
                    name="firstName"
                    control={profileForm.control}
                    render={({ field, fieldState }) => (
                      <Form.Item
                        label="Nombre"
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input {...field} />
                      </Form.Item>
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Controller
                    name="lastName"
                    control={profileForm.control}
                    render={({ field, fieldState }) => (
                      <Form.Item
                        label="Apellidos"
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input {...field} />
                      </Form.Item>
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Controller
                    name="shortName"
                    control={profileForm.control}
                    render={({ field }) => (
                      <Form.Item label="Nombre corto">
                        <Input {...field} />
                      </Form.Item>
                    )}
                  />
                </Col>
                <Col xs={24} md={12}>
                  <Controller
                    name="phone"
                    control={profileForm.control}
                    render={({ field }) => (
                      <Form.Item label="Teléfono">
                        <Input {...field} />
                      </Form.Item>
                    )}
                  />
                </Col>
              </Row>
              <Button type="primary" htmlType="submit" loading={profileMutation.isPending}>
                Guardar
              </Button>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Cambiar contraseña">
            <Form
              layout="vertical"
              onFinish={passwordForm.handleSubmit((v) => passwordMutation.mutate(v))}
            >
              <Controller
                name="currentPassword"
                control={passwordForm.control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Contraseña actual"
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input.Password {...field} autoComplete="current-password" />
                  </Form.Item>
                )}
              />
              <Controller
                name="newPassword"
                control={passwordForm.control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Nueva contraseña"
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input.Password {...field} autoComplete="new-password" />
                  </Form.Item>
                )}
              />
              <Controller
                name="confirmPassword"
                control={passwordForm.control}
                render={({ field, fieldState }) => (
                  <Form.Item
                    label="Confirmar"
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <Input.Password {...field} autoComplete="new-password" />
                  </Form.Item>
                )}
              />
              <Space>
                <Button type="primary" htmlType="submit" loading={passwordMutation.isPending}>
                  Cambiar contraseña
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
}
